/**
 * Everything travelling with the convoy, most credible first.
 *
 * Separate from the alert list on purpose. Alerts answer "what changed"; in
 * dense traffic that is either silent or a wall of near-identical warnings.
 * This answers the question underneath, which an operator asks continuously and
 * the event log cannot: of everything moving with us, which has been doing it
 * longest — because that is the one measure a following vehicle cannot fake and
 * ordinary traffic cannot accidentally produce.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogContent, Divider, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/GppGoodOutlined';
import {
  CompanionTier,
  MissionCotraveler,
  MissionRouteEvent,
  MissionRouteStatus,
  MissionTimelineStop,
  TIER_LABELS,
  listCotravelers,
  promoteCotraveler,
  sendVehicleAlert,
  trustIdentifier,
} from '../../services/missionApi';
import { sendCrewMessage } from '../../services/crewChatApi';
import { formatVehicleCrewLine } from './crewChat';
import { formatMeters, formatSpan, formatWhen } from './format';
import {
  STATE_COLOR, STATE_LABEL, TIER_COLOR,
  cotravelerEvidence, cotravelerName, cotravelerNotes, followerAlertLine,
  isPressing,
} from './cotravelerView';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import OwnKitReview from './OwnKitReview';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ReplayIcon from '@mui/icons-material/Replay';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import MissionThreatReport from './MissionThreatReport';
import { buildThreatReport } from './threatReportView';
import PromoteToPacketDialog, { PromoteTarget } from '../../components/intel/PromoteToPacketDialog';

const GOLD = '#f5b942';

interface Props {
  missionId: number;
  /** Bumped by the host on each monitor tick, so the list follows the run. */
  refreshKey?: number;
  onFocus?: (lat: number, lon: number) => void;
  onCountChange?: (pressing: number, total: number) => void;
  units?: 'km' | 'mi';
  /** The convoy can be spoken to. Lost does not turn this off. */
  canAlert?: boolean;
  onAlerted?: (status: MissionRouteStatus) => void;
  events?: MissionRouteEvent[];
  timeline?: MissionTimelineStop[] | null;
}

const IconAction: React.FC<{
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactElement;
}> = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title}>
    <Box
      component="span"
      role="button"
      tabIndex={disabled ? undefined : 0}
      onClick={() => { if (!disabled) onClick(); }}
      onKeyDown={(e) => { if (!disabled && e.key === 'Enter') onClick(); }}
      sx={{
        display: 'flex',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'text.disabled' : 'text.secondary',
        '&:hover': { color: disabled ? 'text.disabled' : GOLD },
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

const Row: React.FC<{
  cotraveler: MissionCotraveler;
  onFocus?: (lat: number, lon: number) => void;
  onPromote: (c: MissionCotraveler) => void;
  onAlert: (c: MissionCotraveler) => void;
  onExcludeOwn: (c: MissionCotraveler) => void;
  promoting: boolean;
  alerting: boolean;
  excluding: boolean;
  canAlert: boolean;
  units: 'km' | 'mi';
  reportOpen: boolean;
  onToggleReport: () => void;
  events?: MissionRouteEvent[];
  timeline?: MissionTimelineStop[] | null;
  onPromotePacket?: () => void;
}> = ({
  cotraveler: c, onFocus, onPromote, onAlert, onExcludeOwn, promoting, alerting, excluding, canAlert, units,
  reportOpen, onToggleReport, events, timeline, onPromotePacket,
}) => {
  const notes = cotravelerNotes(c);
  const evidence = cotravelerEvidence(c, (m) => formatMeters(m, units), formatSpan);
  const stale = c.state === 'stale';
  const report = reportOpen
    ? buildThreatReport({ events: events || [], cotraveler: c, threatKey: c.threat_key, timeline, units })
    : null;

  return (
    <Box sx={{ py: 0.9 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flex: 1,
          minWidth: 0,
          opacity: stale ? 0.55 : 1,
        }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: STATE_COLOR[c.state] || STATE_COLOR.none,
            }}
          />
          <Typography
            variant="caption"
            onClick={onToggleReport}
            sx={{
              fontWeight: 700,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': { color: GOLD },
            }}
          >
            {cotravelerName(c)}
          </Typography>

          <Chip
            size="small"
            label={TIER_LABELS[c.tier as CompanionTier] || c.tier}
            sx={{
              height: 16,
              fontSize: '0.6rem',
              fontWeight: 700,
              color: TIER_COLOR[c.tier as CompanionTier] || TIER_COLOR.brief,
              borderColor: TIER_COLOR[c.tier as CompanionTier] || TIER_COLOR.brief,
              bgcolor: 'transparent',
              border: '1px solid',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        </Box>

        {c.latitude != null && c.longitude != null && onFocus && (
          <IconAction title="Show where" onClick={() => onFocus(c.latitude!, c.longitude!)}>
            <MyLocationIcon sx={{ fontSize: 14 }} />
          </IconAction>
        )}
        <IconAction
          title="This is ours — stop treating it as a follower"
          onClick={() => onExcludeOwn(c)}
          disabled={excluding}
        >
          <ShieldIcon sx={{ fontSize: 15 }} />
        </IconAction>
        <IconAction
          title={canAlert ? 'Tell the vehicle' : 'Start the mission to reach the vehicle'}
          onClick={() => onAlert(c)}
          disabled={!canAlert || alerting}
        >
          <CampaignIcon sx={{ fontSize: 15 }} />
        </IconAction>
        <IconAction
          title={c.watchlist_id ? 'Already watchlisted' : 'Add to watchlist'}
          onClick={() => onPromote(c)}
          disabled={promoting || !!c.watchlist_id}
        >
          <PlaylistAddIcon sx={{ fontSize: 15 }} />
        </IconAction>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.7, fontSize: '0.66rem' }}>
        {STATE_LABEL[c.state] || c.state}
        {evidence ? ` · ${evidence}` : ''}
        {c.last_observed ? ` · ${formatWhen(c.last_observed)}` : ''}
      </Typography>

      {report && (
        <Box sx={{ pl: 1.7, mt: 0.75, mb: 0.4 }}>
          <MissionThreatReport
            report={report}
            units={units}
            onFocus={onFocus ? (lon, lat) => onFocus(lat, lon) : undefined}
            onPromotePacket={onPromotePacket}
            onMarkOurs={() => onExcludeOwn(c)}
            promoting={promoting}
            marking={excluding}
          />
        </Box>
      )}
      {notes.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ pl: 1.7, mt: 0.35, flexWrap: 'wrap', gap: 0.35 }}>
          {notes.map((note) => (
            <Chip
              key={note}
              size="small"
              label={note}
              sx={{
                height: 15,
                fontSize: '0.575rem',
                bgcolor: 'action.hover',
                '& .MuiChip-label': { px: 0.55 },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const MissionCotravelers: React.FC<Props> = ({
  missionId, refreshKey, onFocus, onCountChange, units = 'mi',
  canAlert = false, onAlerted, events, timeline,
}) => {
  const [list, setList] = useState<MissionCotraveler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [alerting, setAlerting] = useState<string | null>(null);
  const [excluding, setExcluding] = useState<string | null>(null);
  const [showCleared, setShowCleared] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [openReport, setOpenReport] = useState<string | null>(null);
  const [packetTarget, setPacketTarget] = useState<PromoteTarget | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await listCotravelers(missionId, showCleared);
      setList(rows);
      setError(null);
    } catch {
      setError('Could not read co-travellers');
    } finally {
      setLoading(false);
    }
  }, [missionId, showCleared]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    onCountChange?.(
      list.filter((c) => isPressing(c)).length,
      list.length,
    );
  }, [list, onCountChange]);

  const excludeOwn = async (c: MissionCotraveler) => {
    const identifier = (c.identifier || c.threat_key.replace(/^cotraveler:/i, '')).trim();
    if (!identifier) return;
    setExcluding(c.threat_key);
    setError(null);
    try {
      await trustIdentifier(missionId, {
        identifier,
        signal_type: c.signal_type || 'wifi',
        name: c.name,
        manufacturer_id: c.manufacturer_id ?? null,
      });
      await load();
    } catch {
      setError('Could not mark that as ours');
    } finally {
      setExcluding(null);
    }
  };

  const promote = async (c: MissionCotraveler) => {
    setPromoting(c.threat_key);
    try {
      await promoteCotraveler(missionId, c.threat_key, `Promoted from the co-traveller panel`);
      await load();
    } catch {
      setError('Could not add that to the watchlist');
    } finally {
      setPromoting(null);
    }
  };

  const alertVehicle = async (c: MissionCotraveler) => {
    if (!canAlert) return;
    const message = followerAlertLine(c, (m) => formatMeters(m, units));
    const urgent = c.state === 'sustained' || c.tier === 'entrenched';
    setAlerting(c.threat_key);
    setError(null);
    try {
      const status = await sendVehicleAlert(missionId, message, urgent ? 'critical' : 'warning');
      onAlerted?.(status);
      try {
        await sendCrewMessage(formatVehicleCrewLine(message, urgent), 'general', 'alert');
      } catch {
        /* spoken; Chat is secondary */
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not reach the vehicle');
    } finally {
      setAlerting(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4 }}>
            TRAVELLING WITH US
          </Typography>
          <DockTabHelp tab="follow" />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={18} sx={{ color: GOLD }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4 }}>
          TRAVELLING WITH US
        </Typography>
        <DockTabHelp tab="follow" />
        <Box sx={{ flex: 1 }} />
        <IconAction
          title={showCleared
            ? 'This run only'
            : 'Include lost emitters and earlier runs of this mission'}
          onClick={() => setShowCleared((v) => !v)}
        >
          <Chip
            size="small"
            label={showCleared ? 'All' : 'Current'}
            sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }}
          />
        </IconAction>
        <Typography
          variant="caption"
          role="button"
          tabIndex={0}
          onClick={() => setScanOpen(true)}
          sx={{ fontWeight: 700, cursor: 'pointer', color: GOLD, fontSize: '0.65rem' }}
        >
          Own kit
        </Typography>
        <IconAction title="Refresh" onClick={load}>
          <RefreshIcon sx={{ fontSize: 14 }} />
        </IconAction>
      </Stack>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.75 }}>
          {error}
        </Typography>
      )}

      {list.length > 0 && (
        <DockStatStrip
          items={[
            { label: 'Total', value: list.length, color: '#e8ebee', icon: <DirectionsWalkIcon /> },
            { label: 'Pressing', value: list.filter(isPressing).length, color: GOLD, icon: <PersonSearchIcon /> },
            { label: 'Sustained', value: list.filter((c) => c.state === 'sustained').length, color: '#ef5350' },
            { label: 'Confirmed', value: list.filter((c) => c.state === 'confirmed').length, color: '#42a5f5' },
            { label: 'Watchlist', value: list.filter((c) => c.watchlist_id).length, color: '#ef5350', icon: <GppMaybeIcon /> },
            { label: 'Reroute', value: list.filter((c) => c.survived_reroute).length, color: '#00b8a9', icon: <ReplayIcon /> },
            { label: 'Stale', value: list.filter((c) => c.state === 'stale').length, color: '#8a939f' },
            { label: 'Entrenched', value: list.filter((c) => c.tier === 'entrenched').length, color: '#ab47bc' },
          ]}
        />
      )}

      {list.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {showCleared
            ? 'No followers on file for this mission. All includes earlier runs and the alerts that named them; Current is only this run.'
            : 'Nothing is travelling with this convoy yet. An emitter has to share several kilometres of the route across separate minutes before it is counted, so this stays empty for the first part of a movement.'}
        </Typography>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}
          >
            Ordered by how far each has stayed with us, which is the part traffic
            cannot produce by accident.
          </Typography>
          {list.map((c, i) => (
            <React.Fragment key={c.threat_key}>
              {i > 0 && <Divider sx={{ opacity: 0.4 }} />}
              <Row
                cotraveler={c}
                onFocus={onFocus}
                onPromote={promote}
                onAlert={alertVehicle}
                onExcludeOwn={excludeOwn}
                promoting={promoting === c.threat_key}
                alerting={alerting === c.threat_key}
                excluding={excluding === c.threat_key}
                canAlert={canAlert}
                units={units}
                reportOpen={openReport === c.threat_key}
                onToggleReport={() => setOpenReport((id) => (id === c.threat_key ? null : c.threat_key))}
                events={events}
                timeline={timeline}
                onPromotePacket={() => setPacketTarget({
                  member_type: c.watchlist_id ? 'watchlist' : 'device',
                  member_id: String(c.watchlist_id || c.identifier || c.threat_key),
                  label: cotravelerName(c),
                  meta: { threat_key: c.threat_key },
                })}
              />
            </React.Fragment>
          ))}
        </>
      )}

      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
              Own kit on this run
            </Typography>
            <IconButton size="small" onClick={() => setScanOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Start a 60s scan so every assigned device can check in. Then
            exclude anything heard in that minute or the minute before.
          </Typography>
          <OwnKitReview
            missionId={missionId}
            onError={(message) => setError(message)}
            onChanged={() => { load(); }}
          />
        </DialogContent>
      </Dialog>
      <PromoteToPacketDialog
        open={!!packetTarget}
        onClose={() => setPacketTarget(null)}
        target={packetTarget}
        defaultName={packetTarget?.label}
      />
    </Box>
  );
};

export default MissionCotravelers;
